/**
 * Visitor Logger Service
 * Sends visitor analytics data to the backend API
 */

interface VisitorData {
  timestamp: string;
  host: string;
  path: string;
  params: string;
  method: string;
  httpVersion: string;
  userAgent: string;
  referrer: string;
  language: string;
  screenResolution: string;
}

/**
 * Collects visitor data from the browser
 * Note: This collects analytics data including user-agent and referrer.
 * Ensure compliance with privacy regulations (GDPR, etc.) in your backend.
 */
async function collectVisitorData(): Promise<VisitorData> {
  const now = new Date();
  const timestamp = now.toISOString(); // ISO 8601 format with timezone

  return {
    timestamp,
    host: window.location.origin,
    path: window.location.pathname,
    params: window.location.search,
    method: 'GET', // Browser navigation is always GET
    httpVersion: 'http/1.1', // Standard for browsers
    userAgent: navigator.userAgent,
    referrer: document.referrer || 'direct',
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`
  };
}

export async function logVisitor(): Promise<void> {
  try {
    // Skip analytics logging during local development
    if (
      import.meta.env.DEV ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      return;
    }

    const apiBase = (import.meta.env.VITE_DZ_API_BASE ?? '').replace(/\/$/, '');
    
    if (!apiBase) {
      return;
    }

    const visitorData = await collectVisitorData();
    const logUrl = `${apiBase}/log`;

    // Send data to backend (fire-and-forget)
    fetch(logUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(visitorData),
      keepalive: true,
      mode: 'cors',
    }).catch(() => {
      // Silently fail in production if endpoint is unreachable
    });
  } catch {
    // Silently fail
  }
}
